import { useEffect, useMemo, useState } from "react";

import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import PdfRender from "../../pdfRender/pdfRender";

import { sanitizeHtml } from "./utils/sanitizeHtml";

interface TaskDocumentsTabProps {
  /** pharmacy_tasks.task_id (raw, sign-preserved) — the GCS path segment used at upload. */
  taskId: string;
  /** OncoEMR document ids from metadata.attachment_doc_ids. */
  documentIds: string[];
}

type DocKind = "pdf" | "html";

/** One attachment resolved to its download URL ("" when it isn't in storage). */
interface ResolvedDoc {
  docId: string;
  kind: DocKind;
  url: string;
}

/**
 * Build the candidate GCS paths for one attachment.
 *
 * Ingest stores attachments under an extension derived from their content type:
 * PDFs land at `{docId}.pdf`, and the HTML scripts/PA forms the EMR serves inline
 * land at `{docId}.html`.
 *
 * @param taskId - pharmacy_tasks.task_id (raw, sign-preserved)
 * @param docId - OncoEMR document id
 * @returns [pdfPath, htmlPath]
 */
export const attachmentPaths = (taskId: string, docId: string): [string, string] => [
  `onco_tasks/${taskId}/${docId}.pdf`,
  `onco_tasks/${taskId}/${docId}.html`,
];

/**
 * Resolve every attachment to its stored blob: try the PDF path, fall back to the
 * HTML path (a second storage lookup only when the PDF is absent).
 *
 * @param taskId - pharmacy_tasks.task_id (raw, sign-preserved)
 * @param documentIds - OncoEMR document ids from metadata.attachment_doc_ids
 * @returns ResolvedDoc per id, kind "" when the blob is missing entirely
 */
export const resolveDocumentPaths = async (
  taskId: string,
  documentIds: string[],
): Promise<ResolvedDoc[]> => {
  const resolveOne = async (docId: string): Promise<ResolvedDoc> => {
    const [pdfPath, htmlPath] = attachmentPaths(taskId, docId);
    const pdfUrl = await getFileDownloadUrl(pdfPath);
    if (pdfUrl) return { docId, kind: "pdf", url: pdfUrl };
    const htmlUrl = await getFileDownloadUrl(htmlPath);
    return { docId, kind: "html", url: htmlUrl };
  };
  return Promise.all(documentIds.map(resolveOne));
};

/**
 * Documents tab of the task detail modal. Each attachment is uploaded at ingest to
 * a deterministic path in the same Firebase bucket the dashboard reads — PDFs at
 * `onco_tasks/{taskId}/{docId}.pdf`, the HTML scripts/PA forms the EMR serves inline
 * at `{docId}.html` — so we probe both paths, resolve to a download URL, and preview
 * the selected document: PDFs in the PDF viewer, HTML forms in a sandboxed iframe
 * (scripts stripped and sandboxed, so an untrusted form cannot run code). Some
 * attachment ids were never uploaded (their fetch failed) — those resolve to "" and
 * are shown as unavailable rather than as a broken viewer.
 */
const TaskDocumentsTab = ({ taskId, documentIds }: TaskDocumentsTabProps) => {
  const [resolved, setResolved] = useState<ResolvedDoc[] | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [htmlText, setHtmlText] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setResolved(null);
    setSelectedDocId(null);
    setHtmlText(null);
    if (documentIds.length === 0) {
      setResolved([]);
      return;
    }
    resolveDocumentPaths(taskId, documentIds).then((docs) => {
      if (!active) return;
      setResolved(docs);
      setSelectedDocId(docs.find((doc) => doc.url)?.docId ?? null);
    });
    return () => {
      active = false;
    };
  }, [taskId, documentIds]);

  const selected = useMemo(
    () => resolved?.find((doc) => doc.docId === selectedDocId) ?? null,
    [resolved, selectedDocId],
  );
  const selectedUrl = selected?.url ?? "";

  // HTML forms render as text in a sandboxed iframe: fetch the stored HTML, strip
  // scripts/styles and inline handlers, and neutralize javascript: URLs.
  useEffect(() => {
    let active = true;
    setHtmlText(null);
    if (!selected || selected.kind !== "html" || !selected.url) return;
    fetch(selected.url)
      .then((resp) => (resp.ok ? resp.text() : ""))
      .then((text) => {
        if (!active || !text) return;
        setHtmlText(sanitizeHtml(text));
      })
      .catch(() => {
        if (active) setHtmlText("");
      });
    return () => {
      active = false;
    };
  }, [selected]);

  if (resolved === null) {
    return <div className="text-small text-primaryGray-7">Loading documents…</div>;
  }

  if (resolved.length === 0) {
    return <p className="text-small text-primaryGray-7">No documents attached.</p>;
  }

  return (
    <div className="flex h-full min-h-0 gap-4">
      {/* Left: document list */}
      <div className="flex w-[34%] shrink-0 flex-col gap-2 overflow-auto border-r border-primaryGray-15 pr-4">
        {resolved.map((doc, index) => {
          const available = Boolean(doc.url);
          const isSelected = doc.docId === selectedDocId;
          return (
            <button
              key={doc.docId}
              type="button"
              disabled={!available}
              onClick={() => setSelectedDocId(doc.docId)}
              className={`flex flex-col items-start gap-0.5 rounded border px-3 py-2 text-left ${
                isSelected
                  ? "border-tertiaryBlue-5 text-tertiaryBlue-5"
                  : "border-primaryGray-14 text-primaryGray-2"
              } ${available ? "cursor-pointer hover:border-tertiaryBlue-5" : "cursor-not-allowed opacity-60"}`}
            >
              <span className="text-small font-semiBold">Document {index + 1}</span>
              <span className="w-full truncate text-tiny text-primaryGray-7">
                {available ? doc.docId : `${doc.docId} (unavailable)`}
              </span>
            </button>
          );
        })}
      </div>

      {/* Right: preview of the selected document (PDF viewer or sandboxed HTML form) */}
      <div className="flex min-h-0 flex-1 flex-col">
        {selected?.kind === "html" && selectedUrl ? (
          <div className="min-h-0 flex-1 overflow-auto bg-white">
            {htmlText === null ? (
              <div className="p-4 text-small text-primaryGray-7">Loading document…</div>
            ) : htmlText ? (
              <iframe
                title={`Document ${selectedDocId}`}
                sandbox=""
                srcDoc={htmlText}
                className="h-full w-full border-0"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <p className="text-small text-primaryGray-7">This document is not available to preview.</p>
              </div>
            )}
          </div>
        ) : selectedUrl ? (
          <>
            <div className="mb-2 flex shrink-0 justify-end">
              <a
                href={selectedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-tiny font-semiBold text-tertiaryBlue-5 hover:underline"
              >
                Open in new tab ↗
              </a>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <PdfRender file={selectedUrl} />
            </div>
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-small text-primaryGray-7">This document is not available to preview.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDocumentsTab;